import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Menu as MenuIcon,
  Users,
  ShoppingCart,
  Clock,
  Settings,
  QrCode,
  Store,
  Receipt,
  Eye,
  LogOut,
  Activity,
  MessageSquare,
  Bell,
} from "lucide-react";
import { useBrandStore } from "@/store/brandStore";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/authStore";
import { cn } from "@/lib/currencyFormatter";
import { BlinkingDot } from "@/components/ui/blinking-dot";
import { useOrderStore } from "@/store/orderStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useChatStore } from "@/store/chat";
import { useNotificationStore } from "@/store/notificationStore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { StoreSelector } from "@/components/menu/StoreSelector";
import { isNavItemAllowed } from "@/utils/roleUtils";

interface MobileNavDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileNavDrawer({ open, onOpenChange }: MobileNavDrawerProps) {
  const location = useLocation();
  const { logout } = useAuthStore();
  const { name, logo } = useBrandStore();
  const { pendingOrdersCount, updatePendingOrdersCount } = useOrderStore();
  const { stores, selectedStoreId, setSelectedStoreId } =
    useStoreManagementStore();
  const { unreadCount } = useChatStore();
  const { unreadCount: notificationCount } = useNotificationStore();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuthStore();
  // Calculate the number of stores
  const noStores = stores.length === 0;

  // Update the pending orders count when the component mounts
  useEffect(() => {
    updatePendingOrdersCount();
  }, [updatePendingOrdersCount]);

  // Close drawer when route changes
  useEffect(() => {
    onOpenChange(false);
  }, [location.pathname, onOpenChange]);

  const navigation = [
    {
      icon: Home,
      label: t("dashboard"),
      href: "/dashboard",
      path: "dashboard",
    },
    {
      icon: MenuIcon,
      label: t("menuManagement.title"),
      href: "/dashboard/menu",
      path: "menu",
    },
    {
      icon: QrCode,
      label: t("qrAndTables"),
      href: "/dashboard/tables",
      path: "tables",
    },
    {
      icon: ShoppingCart,
      label: t("orders"),
      href: "/dashboard/orders",
      orderCount: pendingOrdersCount,
      path: "orders",
    },
    {
      icon: Clock,
      label: t("orderHistory"),
      href: "/dashboard/history",
      path: "history",
    },
    {
      icon: Receipt,
      label: t("invoiceAndSales"),
      href: "/dashboard/invoice-sales",
      path: "invoice-sales",
    },
    {
      icon: Activity,
      label: t("activityLog"),
      href: "/dashboard/activity",
      path: "activity",
    },
    {
      icon: Bell,
      label: t("notifications.title"),
      href: "/dashboard/notifications",
      notificationCount: notificationCount,
      path: "notifications",
    },
    {
      icon: Store,
      label: t("storeManagement.title"),
      href: "/dashboard/stores",
      showDot: noStores,
      path: "stores",
    },
    { icon: Users, label: t("staff"), href: "/dashboard/staff", path: "staff" },

    {
      icon: Users,
      label: t("attendance"),
      href: "/dashboard/attendance",
      path: "attendance",
    },
    {
      icon: MessageSquare,
      label: t("chat.title"),
      href: "/dashboard/chat",
      messageCount: unreadCount,
      path: "chat",
    },
    {
      icon: Eye,
      label: t("menuSandbox"),
      href: "/dashboard/menu-sandbox",
      path: "menu-sandbox",
    },
    {
      icon: Settings,
      label: t("settings"),
      href: "/dashboard/settings",
      path: "settings",
    },
  ].filter((item) => isNavItemAllowed(user?.role, item.path));

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      // Prevent multiple clicks
      if (isLoggingOut) return;

      setIsLoggingOut(true);

      // Show toast
      toast({
        title: "Logging out",
        description: "You have been successfully logged out.",
        duration: 3000,
      });

      // Close dialog immediately to improve responsiveness
      setLogoutDialogOpen(false);

      // Perform logout
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Logout error",
        description: "There was a problem logging you out. Please try again.",
        variant: "destructive",
      });
      setIsLoggingOut(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="h-[85vh]">
        <DrawerHeader className="border-b">
          <DrawerTitle className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt={name} className="h-6" />
            ) : (
              <span>{t("Xerven")}</span>
            )}
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-4 py-2 border-b">
          <StoreSelector
            stores={stores}
            value={selectedStoreId || ""}
            onValueChange={setSelectedStoreId}
            disabled={stores.length === 0}
            selectedStoreId={selectedStoreId}
            className="w-full"
          />
        </div>

        <div className="flex-1 overflow-auto py-2">
          <nav className="grid gap-1 px-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <DrawerClose asChild key={item.href}>
                  <Link to={item.href}>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full justify-start gap-3",
                        isActive
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground"
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="flex-1">{item.label}</span>
                      {item.showDot && <BlinkingDot />}
                      {item.orderCount !== undefined && item.orderCount > 0 && (
                        <BlinkingDot count={item.orderCount} />
                      )}
                      {item.messageCount !== undefined &&
                        item.messageCount > 0 && (
                          <BlinkingDot count={item.messageCount} />
                        )}
                      {item.notificationCount !== undefined &&
                        item.notificationCount > 0 && (
                          <BlinkingDot count={item.notificationCount} />
                        )}
                    </Button>
                  </Link>
                </DrawerClose>
              );
            })}
          </nav>
        </div>

        <DrawerFooter className="border-t pt-4">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground"
            onClick={handleLogoutClick}
            disabled={isLoggingOut}
          >
            <LogOut className="h-5 w-5" />
            {isLoggingOut ? t("loggingOut") : t("logout")}
          </Button>
        </DrawerFooter>
      </DrawerContent>

      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("logoutConfirmationTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("logoutConfirmationDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutConfirm}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? t("loggingOut") : t("logout")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Drawer>
  );
}
